/**
 * Raise credit note (docs/features/order-to-invoice/
 * 80_finance_sales_ledger_sl4_update_plan.md commit 7; finance, `sales_ledger:manage`):
 * which lines of an invoice to credit and by how much, why, and where the
 * money goes.
 *
 * No mock-up was drawn: this is the smallest dialog in the language of Record
 * payment and Set VAT. It offers only what the ledger will accept: the options
 * the invoice page carried (`CreditOptions`, the ledger's own rule), so a
 * method the invoice was not paid by, or one already refunded in full, is
 * shown unavailable with the reason. A refusal on one input comes back bound
 * to that input (api-validation.md), with a banner that counts them.
 *
 * The reason is internal: the customer's credit note lists the credited lines
 * and never says why (Ben, 6 Oct). The dialog tells finance so. The VAT on a
 * part credit is the ledger's to work out, so the summary gives the gross only
 * when every line is credited in full.
 */
import { useId, useState } from "react";
import type {
  CreditOptions,
  RaiseCreditNoteInput,
} from "@shared-core/data/sales-ledger-types";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { newUuid } from "@shared-core/domain/object-ui/command-run";
import {
  londonTodayIso,
  type OrderStepFailure,
  type OrderStepField,
} from "@shared-core/domain/order-to-invoice-presentation";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import { Textarea } from "../../primitives/textarea";
import { FailureBanner, Field } from "../orders/OrderDialogs";

type Refund = NonNullable<RaiseCreditNoteInput["refund"]>;

const MAX_REASON_LENGTH = 500;
const MAX_REFERENCE_LENGTH = 255;

/** "25", "25.5" or "25.50" -> 2500 pence; null when it isn't a price. */
function penceOf(pounds: string): number | null {
  const text = pounds.trim().replace(/^£/, "").replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  return Math.round(Number(text) * 100);
}

const poundsOf = (pence: number) => (pence / 100).toFixed(2);

interface LineChoice {
  isChosen: boolean;
  amount: string;
}

type Problems = Partial<Record<OrderStepField, string>>;

export function RaiseCreditNoteDialog({
  invoiceNo,
  options,
  initialLineIds = [],
  busy,
  onClose,
  onRaise,
}: {
  invoiceNo: string;
  options: CreditOptions;
  /** Lines ticked when the dialog opens: the order page's *Credit…* on one line (doc 98 §4). */
  initialLineIds?: string[];
  busy: boolean;
  onClose: () => void;
  onRaise: (input: RaiseCreditNoteInput) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const today = londonTodayIso();
  // One id per dialog: a second click submits the same credit note, never another.
  const [requestId] = useState(() => newUuid());
  const creditable = options.lines.filter(
    (l) => l.remaining_net_pence > 0 || l.remaining_vat_pence > 0,
  );
  const [choices, setChoices] = useState<Record<string, LineChoice>>(() =>
    Object.fromEntries(
      creditable.map((l) => [
        l.invoice_line_id,
        {
          isChosen: initialLineIds.includes(l.invoice_line_id),
          // A cancelled rest's proposed credit is filled in for finance to change (doc 98 §3.3).
          amount: poundsOf(
            (l.proposed_net_pence ?? 0) > 0
              ? l.proposed_net_pence!
              : l.remaining_net_pence,
          ),
        },
      ]),
    ),
  );
  const [reason, setReason] = useState("");
  const [refund, setRefund] = useState<Refund>("none");
  const [refundedOn, setRefundedOn] = useState(today);
  const [bankReference, setBankReference] = useState("");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);

  const methods: {
    value: Refund;
    label: string;
    hint: string;
    isOffered: boolean;
  }[] = [
    {
      value: "none",
      label: "No refund",
      hint: "It reduces what the customer owes. Any credit left stays on their account in Xero.",
      isOffered: true,
    },
    {
      value: "card",
      label: "Refund to the card",
      hint:
        options.refundable_by_card_pence > 0
          ? `Up to ${formatQuotedPrice(options.refundable_by_card_pence)} can go back to the card now.`
          : options.paid_by_card_pence === 0
            ? "The invoice was not paid by card."
            : "Everything paid by card has already been refunded.",
      isOffered: options.refundable_by_card_pence > 0,
    },
    {
      value: "bank_transfer",
      label: "Refund by bank transfer",
      hint:
        options.refundable_by_bank_transfer_pence > 0
          ? `You have paid up to ${formatQuotedPrice(options.refundable_by_bank_transfer_pence)} back from the bank: record it here.`
          : options.paid_by_bank_transfer_pence === 0
            ? "The invoice was not paid by bank transfer."
            : "Everything paid by bank transfer has already been refunded.",
      isOffered: options.refundable_by_bank_transfer_pence > 0,
    },
  ];

  const chosen = creditable.filter((l) => choices[l.invoice_line_id]?.isChosen);
  const amounts = chosen.map((l) => ({
    line: l,
    pence: penceOf(choices[l.invoice_line_id]!.amount),
  }));
  const isEveryLineInFull =
    amounts.length > 0 &&
    amounts.every((a) => a.pence === a.line.remaining_net_pence);

  const problems: Problems = {
    lines:
      chosen.length === 0 ? "Choose at least one line to credit." : undefined,
    reason: !reason.trim()
      ? "Say why the credit note is raised."
      : reason.trim().length > MAX_REASON_LENGTH
        ? `A reason is at most ${MAX_REASON_LENGTH} characters.`
        : undefined,
    "bank_refund.refunded_on":
      refund === "bank_transfer" && !refundedOn
        ? "Enter the day the money was paid back."
        : refund === "bank_transfer" && refundedOn > today
          ? "The date can't be in the future."
          : undefined,
    "bank_refund.reference":
      refund === "bank_transfer" &&
      bankReference.trim().length > MAX_REFERENCE_LENGTH
        ? `A reference is at most ${MAX_REFERENCE_LENGTH} characters.`
        : undefined,
  };
  amounts.forEach(({ line, pence }, position) => {
    const key = `lines.${position}.net_pence` as const;
    problems[key] =
      pence === null || pence <= 0
        ? "Enter the net amount to credit, e.g. 120.00."
        : pence > line.remaining_net_pence
          ? `At most ${formatQuotedPrice(line.remaining_net_pence)} is left to credit.`
          : undefined;
  });
  const problemCount = Object.values(problems).filter(Boolean).length;
  const errorOf = (field: OrderStepField) =>
    failure?.fieldErrors[field] ?? (failure ? problems[field] : undefined);

  const set = (lineId: string, change: Partial<LineChoice>) =>
    setChoices((current) => ({
      ...current,
      [lineId]: { ...current[lineId]!, ...change },
    }));

  const net = amounts.reduce((sum, a) => sum + (a.pence ?? 0), 0);
  const vat = isEveryLineInFull
    ? amounts.reduce((sum, a) => sum + a.line.remaining_vat_pence, 0)
    : null;
  const where =
    refund === "card"
      ? ", refunded to the card"
      : refund === "bank_transfer"
        ? ", refunded by bank transfer"
        : ", reducing what is owed";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Raise credit note against {invoiceNo}</DialogTitle>
          <DialogDescription>
            Finance only. Credit the lines below in full or in part. The
            customer&apos;s credit note lists the lines credited and never says
            why: the reason is for us.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            if (problemCount > 0) {
              setFailure({
                summary:
                  problemCount === 1
                    ? "One field needs correcting."
                    : `${problemCount} fields need correcting.`,
                fieldErrors: {},
              });
              return;
            }
            const refused = await onRaise({
              lines: amounts.map(({ line, pence }) => ({
                invoice_line_id: line.invoice_line_id,
                // Left out, the ledger credits everything left on the line, VAT included.
                ...(pence === line.remaining_net_pence
                  ? {}
                  : { net_pence: pence }),
              })),
              reason: reason.trim(),
              refund,
              request_id: requestId,
              ...(refund === "bank_transfer"
                ? {
                    bank_refund: {
                      refunded_on: refundedOn,
                      ...(bankReference.trim()
                        ? { reference: bankReference.trim() }
                        : {}),
                    },
                  }
                : {}),
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />

          <fieldset
            className="space-y-2"
            aria-describedby={
              errorOf("lines") ? `${id}-lines-error` : undefined
            }
          >
            <legend className="text-sm font-medium">Lines to credit</legend>
            {creditable.map((line) => {
              const choice = choices[line.invoice_line_id]!;
              const position = chosen.findIndex(
                (l) => l.invoice_line_id === line.invoice_line_id,
              );
              const amountError =
                position >= 0
                  ? errorOf(`lines.${position}.net_pence`)
                  : undefined;
              return (
                <div
                  key={line.invoice_line_id}
                  className="space-y-1.5 rounded-md border border-border p-2"
                >
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id={`${id}-${line.invoice_line_id}`}
                      checked={choice.isChosen}
                      onCheckedChange={(checked) =>
                        set(line.invoice_line_id, {
                          isChosen: checked === true,
                        })
                      }
                    />
                    <Label
                      htmlFor={`${id}-${line.invoice_line_id}`}
                      className="flex-1 font-normal"
                    >
                      <span className="tabular-nums text-muted-foreground">
                        {line.line_no}
                      </span>{" "}
                      {line.description}
                      <span className="block text-xs text-muted-foreground">
                        {formatQuotedPrice(line.remaining_net_pence)} net +{" "}
                        {formatQuotedPrice(line.remaining_vat_pence)} VAT left
                        to credit
                        {line.credited_net_pence > 0 &&
                          `, ${formatQuotedPrice(line.credited_net_pence)} net already credited`}
                      </span>
                    </Label>
                  </div>
                  {choice.isChosen && (
                    <div className="flex flex-wrap items-end gap-2 pl-6">
                      <Field
                        id={`${id}-${line.invoice_line_id}-amount`}
                        label={`Net to credit on ${line.line_no} (£)`}
                        error={amountError}
                      >
                        {(aria) => (
                          <Input
                            {...aria}
                            inputMode="decimal"
                            className="w-32"
                            value={choice.amount}
                            onChange={(e) =>
                              set(line.invoice_line_id, {
                                amount: e.target.value,
                              })
                            }
                          />
                        )}
                      </Field>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          set(line.invoice_line_id, {
                            amount: poundsOf(line.remaining_net_pence),
                          })
                        }
                      >
                        Credit in full
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            {errorOf("lines") && (
              <p id={`${id}-lines-error`} className="text-sm text-red-600">
                {errorOf("lines")}
              </p>
            )}
          </fieldset>

          <Field
            id={`${id}-reason`}
            label="Reason"
            error={errorOf("reason")}
            hint="Internal: it is never printed on the customer's credit note."
          >
            {(aria) => (
              <Textarea
                {...aria}
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">
              Where the money goes
            </legend>
            <RadioGroup
              value={refund}
              onValueChange={(v) => setRefund(v as Refund)}
              aria-label="Where the money goes"
            >
              {methods.map((m) => (
                <div key={m.value} className="flex items-start gap-2">
                  <RadioGroupItem
                    value={m.value}
                    id={`${id}-refund-${m.value}`}
                    disabled={!m.isOffered}
                    aria-describedby={`${id}-refund-${m.value}-hint`}
                  />
                  <div>
                    <Label
                      htmlFor={`${id}-refund-${m.value}`}
                      className={m.isOffered ? "" : "text-muted-foreground"}
                    >
                      {m.label}
                    </Label>
                    <p
                      id={`${id}-refund-${m.value}-hint`}
                      className="text-xs text-muted-foreground"
                    >
                      {m.hint}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>
            {errorOf("refund") && (
              <p className="text-sm text-red-600">{errorOf("refund")}</p>
            )}
          </fieldset>

          {refund === "bank_transfer" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                id={`${id}-refunded-on`}
                label="Paid back on"
                error={errorOf("bank_refund.refunded_on")}
              >
                {(aria) => (
                  <Input
                    {...aria}
                    type="date"
                    value={refundedOn}
                    max={today}
                    onChange={(e) => setRefundedOn(e.target.value)}
                  />
                )}
              </Field>
              <Field
                id={`${id}-bank-reference`}
                label="Bank reference (optional)"
                error={errorOf("bank_refund.reference")}
              >
                {(aria) => (
                  <Input
                    {...aria}
                    value={bankReference}
                    maxLength={MAX_REFERENCE_LENGTH}
                    onChange={(e) => setBankReference(e.target.value)}
                  />
                )}
              </Field>
            </div>
          )}

          <p className="text-sm text-muted-foreground" aria-live="polite">
            {chosen.length === 0
              ? "Choose a line to see the credit note it will make."
              : vat === null
                ? `A credit note for ${formatQuotedPrice(net)} net plus VAT${where}.`
                : `A credit note for ${formatQuotedPrice(net)} net + ${formatQuotedPrice(vat)} VAT = ${formatQuotedPrice(net + vat)}${where}.`}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Raise credit note
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
